import * as Logger from './logger.js'
import { HSTable, MISSING_BASELINE_SENTINEL } from './table.js';
import { HSCard } from './card.js';
import { Auth } from './auth.js';
import { UserSession } from './session.js';
import * as Utils from './utils.js';

const STATS_SSE_RETRY_ATTEMPTS = 3;
const STATS_SSE_RETRY_DELAY_MS = 3000;
const DEFAULT_COMPARE_MODE = 'ROLLING_WINDOW';
const STATS_AUTO_INVALIDATION_ENABLED = false;

export class HSStatsTable extends HSTable {
    currentData = [];
    baselineData = [];
    invalidateTimer = null;
    isReloading = false;
    pendingReload = false;
    baseStatsColumns = [];
    activeColumnPreset = 'summary';
    lastTimeScope = null;

    constructor() {
        super();
    }

    onLoad() {
        super.onLoad();
        // Listen for snapshot changes
        this.onEvent('hs-snapshot-changed', (opts) => {
            Logger.debug("Snapshot changed, reloading stats: ", opts);
            this.reloadData();
        });
        this.onEvent('hs-stats-column-preset-changed', (opts) => {
            this.applyColumnPreset(opts?.preset);
        });
    }

    onUnload() {
        if (this.invalidateTimer) {
            clearTimeout(this.invalidateTimer);
            this.invalidateTimer = null;
        }
        super.onUnload();
    }

    onUpdate(update) {
        if (!STATS_AUTO_INVALIDATION_ENABLED) {
            return;
        }
        if (update.subscription_version != this.sseSubscriptionVersion) {
            Logger.warn("Discarding stale stats invalidation update", update);
            return;
        }
        if (update?.obj?.type !== 'invalidate') {
            Logger.debug("Ignoring non-invalidation update for stats view", update);
            return;
        }
        this.scheduleInvalidationReload(update.obj?.source_name);
    }

    scheduleInvalidationReload(sourceName = 'unknown') {
        const debounceMs = 2000;
        if (this.invalidateTimer) {
            clearTimeout(this.invalidateTimer);
        }
        Logger.debug("Stats invalidated by source, scheduling refetch", { sourceName, debounceMs });
        this.invalidateTimer = setTimeout(async () => {
            this.invalidateTimer = null;
            if (this.isReloading) {
                this.pendingReload = true;
                return;
            }
            await this.reloadData();
        }, debounceMs);
    }

    async extractRequestErrorInfo(err) {
        const fallback = {
            status: err?.response?.status || null,
            code: null,
            message: err?.message || 'Unknown request error',
        };

        const response = err?.response;
        if (!response || typeof response.json !== 'function') {
            return fallback;
        }

        try {
            const body = await response.json();
            const payload = body?.error || {};
            return {
                status: response.status || fallback.status,
                code: payload.code || null,
                message: payload.message || fallback.message,
            };
        } catch (parseErr) {
            Logger.warn('Failed to parse stats source error response payload', parseErr);
            return fallback;
        }
    }

    showStatsError(info = {}) {
        const code = info?.code || 'UNKNOWN';
        const status = info?.status ? `HTTP ${info.status}` : 'Request failed';
        const row = {
            id: 'stats_error_row',
            providerid: code,
            displayname: `Unable to load stats data. Please report error code: ${code}`,
            total_hubs: '',
            deployed: '',
            spare: '',
            demo: '',
            all_calls: '',
            staff_calls: '',
            media_shared: '',
        };
        this.dataView.setItems([ row ]);
        Logger.error('Stats view user-facing error', {
            status,
            code,
            message: info?.message || 'Unknown error',
        });
    }

    async sleep(ms = 0) {
        return await new Promise(resolve => setTimeout(resolve, ms));
    }

    async recoverSseAndRetrySubscription({ buildSubscriptionPath, reason, subscriptionId }) {
        let lastError = null;
        for (let attempt = 1; attempt <= STATS_SSE_RETRY_ATTEMPTS; attempt++) {
            try {
                Logger.warn('Retrying stats SSE reconnect and subscription', {
                    attempt,
                    maxAttempts: STATS_SSE_RETRY_ATTEMPTS,
                    retryDelayMs: STATS_SSE_RETRY_DELAY_MS,
                    reason,
                    subscriptionId,
                });

                UserSession.set('sseStreamId', null);
                await Auth.sseConnect();
                const nextStreamId = UserSession.get('sseStreamId');
                if (!nextStreamId) {
                    throw new Error('SSE reconnect did not provide a stream id');
                }

                await this.fetch(buildSubscriptionPath(nextStreamId));
                Logger.info('Stats invalidation subscription registered after SSE reconnect', {
                    attempt,
                    subscriptionId,
                });
                return true;
            } catch (err) {
                lastError = err;
                Logger.warn('Stats SSE reconnect/subscription attempt failed', {
                    attempt,
                    maxAttempts: STATS_SSE_RETRY_ATTEMPTS,
                    reason,
                    error: err?.message || String(err),
                });
                if (attempt < STATS_SSE_RETRY_ATTEMPTS) {
                    await this.sleep(STATS_SSE_RETRY_DELAY_MS);
                }
            }
        }

        Auth.sseCleanup();
        Logger.error('Stats invalidation subscription recovery failed after retries; SSE state torn down', {
            attempts: STATS_SSE_RETRY_ATTEMPTS,
            retryDelayMs: STATS_SSE_RETRY_DELAY_MS,
            reason,
            subscriptionId,
            error: lastError?.message || String(lastError || 'unknown error'),
        });
        return false;
    }

    async ensureInvalidationSubscription() {
        if (!STATS_AUTO_INVALIDATION_ENABLED) {
            return;
        }
        let streamId = UserSession.get('sseStreamId');
        if (this.subscribed && this.sseSubscriptionId) {
            return;
        }

        if (!this.sseSubscriptionId) {
            HSCard.sseIdCounter++;
            this.sseSubscriptionId = HSCard.sseIdCounter;
        }

        const sourceList = [
            'rollup.stats.user.accounts.monthly',
            'rollup.homesight.call_records_by_time.daily',
            'rollup.huma.user_resource_share.daily',
        ];

        const subscriptions = UserSession.get('sseSubscriptions') || {};
        subscriptions[this.sseSubscriptionId] = {
            onSubscribed: this.onSubscribed.bind(this),
            onUpdate: this.onUpdate.bind(this),
            resubscribe: async (activeStreamId) => {
                await this.fetch(buildSubscriptionPath(activeStreamId));
            },
        };
        UserSession.set('sseSubscriptions', subscriptions);

        const buildSubscriptionPath = (activeStreamId) => [
            'api/rollup/stats/user/accounts/monthly',
            `subscribe=${activeStreamId}:${this.sseSubscriptionId}`,
            'subscribe_mode=invalidate',
            `subscribe_sources=${encodeURIComponent(sourceList.join(','))}`,
            'subscribe_debounce_ms=2000',
            'subscribe_only=true',
            `rid=${Math.random()}`,
        ].join('&').replace('monthly&', 'monthly?');

        if (!streamId) {
            Logger.warn('Stats invalidation subscription missing SSE stream id; attempting reconnect');
            const recovered = await this.recoverSseAndRetrySubscription({
                buildSubscriptionPath,
                reason: 'missing-sse-stream-id',
                subscriptionId: this.sseSubscriptionId,
            });
            if (recovered) {
                this.subscribed = true;
                return;
            }
            delete subscriptions[this.sseSubscriptionId];
            UserSession.set('sseSubscriptions', subscriptions);
            this.subscribed = false;
            this.sseSubscriptionVersion = 0;
            return;
        }

        try {
            await this.fetch(buildSubscriptionPath(streamId));
            this.subscribed = true;
            Logger.info('Stats invalidation subscription registered', {
                subscriptionId: this.sseSubscriptionId,
                sourceList,
            });
        } catch (err) {
            if (err?.response?.status === 404) {
                Logger.warn('Stats invalidation subscribe found stale SSE session; starting retry recovery loop', {
                    previousStreamId: streamId,
                    subscriptionId: this.sseSubscriptionId,
                });
                const recovered = await this.recoverSseAndRetrySubscription({
                    buildSubscriptionPath,
                    reason: 'stale-sse-session-404',
                    subscriptionId: this.sseSubscriptionId,
                });
                if (recovered) {
                    this.subscribed = true;
                    return;
                }
            }
            Logger.warn('Failed to register stats invalidation subscription', err);
            delete subscriptions[this.sseSubscriptionId];
            UserSession.set('sseSubscriptions', subscriptions);
            this.subscribed = false;
            this.sseSubscriptionVersion = 0;
        }
    }

    async reloadData() {
        Logger.debug("HSStatsTable.reloadData called");
        if (this.isReloading) {
            this.pendingReload = true;
            return;
        }
        this.isReloading = true;
        
        try {
            if (STATS_AUTO_INVALIDATION_ENABLED) {
                await this.ensureInvalidationSubscription();
            }

            // Get the time scope to determine the analysis window
            const timeScope = UserSession.get('timeScope') || 'month';

            if (this.lastTimeScope !== null && this.lastTimeScope !== timeScope) {
                const resetAnchor = this.getDefaultSnapshotAnchor(timeScope);
                UserSession.set('statsSnapshotAnchor', resetAnchor);
            }
            this.lastTimeScope = timeScope;
            
            // Debug: log the full config object
            Logger.debug('HSStatsTable full config object: ', {
                configExists: !!this.config,
                configKeys: this.config ? Object.keys(this.config) : [],
                fullConfig: this.config,
            });

            // Get the snapshot anchor from session or use the current scope default
            let snapshotAnchor = Number(UserSession.get('statsSnapshotAnchor'));
            if (!snapshotAnchor) {
                snapshotAnchor = this.getDefaultSnapshotAnchor(timeScope);
                UserSession.set('statsSnapshotAnchor', snapshotAnchor);
            }

            Logger.debug("Stats view parameters: ", {
                snapshotAnchor,
                timeScope,
                compareMode: this.getCompareMode(),
            });

            const selectedProviders = UserSession.get('selected_providers') || [];
            const filteredProviders = UserSession.get('filtered_providers') || [];
            const selectedOrgs = UserSession.get('selected_orgs') || [];
            const userType = UserSession.get('userinfo')?.usertype;
            const providers = selectedProviders.length > 0
                ? selectedProviders
                : filteredProviders;

            // Ensure reportId is set (handle empty strings and undefined)
            const reportId = (this.config?.reportId ?? '').trim() || 'providers_stats';
            const windowSize = this.getWindowSize(timeScope);

            Logger.debug('Stats card config:', {
                configReportId: this.config?.reportId,
                timeScope: timeScope,
                calculatedReportId: reportId,
                calculatedWindowSize: windowSize,
                providersCount: providers?.length || 0,
            });

            const now = new Date();
            const nowTs = Date.now();
            const todayStartUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0);
            const currentPeriodAnchor = Number(this.getDefaultSnapshotAnchor(timeScope));
            const anchorMeta = this.getAnchorMeta(timeScope);

            // For day scope, the picker produces midnight UTC anchors while the default anchor is the
            // current UTC hour. Detect "today" by comparing UTC date components instead of exact value.
            const isCurrentPeriodAnchor = timeScope === 'day'
                ? Number(snapshotAnchor) >= todayStartUtc
                : Number(snapshotAnchor) === currentPeriodAnchor;

            let effectiveAsOf;
            if (timeScope === 'day') {
                if (isCurrentPeriodAnchor) {
                    // Today: use the live current UTC hour
                    effectiveAsOf = Utils.getCurrentUTCHour();
                } else {
                    // Historical day: use 23:00 UTC of the selected day so the server
                    // sees a full-day window rather than a zero-width window at midnight.
                    const d = new Date(snapshotAnchor);
                    effectiveAsOf = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 0, 0, 0);
                }
            } else {
                const useLiveCurrentCutoff = [ 'week', 'month', 'quarter' ].includes(timeScope)
                    && (anchorMeta.isCurrentPeriod || isCurrentPeriodAnchor);
                effectiveAsOf = useLiveCurrentCutoff
                    ? nowTs
                    : snapshotAnchor;
            }

            const payload = {
                reportId: reportId,
                scope: timeScope,
                asOf: effectiveAsOf,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
                compareMode: this.getCompareMode(),
                windowSize: windowSize,
                orgIds: selectedOrgs.map((org) => org?.id || org?.orgid).filter(Boolean),
                entities: (providers || []).map((provider) => ({
                    providerid: provider.id || provider.providerid,
                    displayname: provider.displayname || provider.name || provider.id || provider.providerid,
                })),
            };

            const isPrivilegedAllScopeUser = userType === 'super' || userType === 'ops' || userType === 'developer' || userType === 'devops';
            if (reportId === 'providers_stats' && isPrivilegedAllScopeUser && selectedProviders.length === 0 && selectedOrgs.length === 0) {
                payload.entityScope = 'ALL_ACCESSIBLE';
            }

            Logger.debug('Stats metric-table report payload: ', payload);
            const report = await this.fetch('api/reports/metric-table', { method: 'POST', payload });
            const mergedData = Array.isArray(report?.rows) ? report.rows : [];
            const providerLookup = new Map(
                (UserSession.get('providers') || []).map(provider => [provider.id || provider.providerid, provider])
            );
            mergedData.forEach((row) => {
                const provider = providerLookup.get(row.providerid);
                if (!row.displayname) {
                    row.displayname = provider?.displayname || provider?.name || provider?.providername || row.providerid || '';
                }
                if (!row.providername) {
                    row.providername = provider?.providername || row.displayname || provider?.name || provider?.displayname || row.providerid || '';
                }
                if (!row.territory_id && provider?.territory_id) {
                    row.territory_id = provider.territory_id;
                }
            });
            Logger.debug('Stats metric-table report response meta: ', {
                compareMode: report?.compareMode,
                compareLabel: report?.compareLabel,
                completeness: report?.completeness,
                isPartial: report?.isPartial,
                rowCount: mergedData.length,
            });

            // Set up row IDs
            if (this.config.idField) {
                if (this.config.idField == 'auto') {
                    mergedData.forEach((row, index) => { row.id = `id_${index}`; });
                } else {
                    mergedData.forEach((row, index) => { 
                        row.id = row[this.config.idField] ? row[this.config.idField] : `id_${index}`; 
                    });
                }
            } else {
                // Default: use providerid as ID
                mergedData.forEach((row, index) => { 
                    row.id = row.providerid || `id_${index}`; 
                });
            }

            if (this.config.showTotalsRow) {
                const totalsRow = this.computeTotalsRow(mergedData, this.config.cols);
                mergedData.unshift(totalsRow);
                this.installTotalsRowHooks();
            }

            this.dataView.setItems(mergedData);
            this.currentData = mergedData;
            this.updateComparisonLabel();
            this.updatePreHeaderGroups();
            this.hideLoading();
            this.redraw();

        } catch (err) {
            Logger.error("Error reloading stats data: ", err);
            const info = await this.extractRequestErrorInfo(err);
            this.showStatsError(info);
            this.hideLoading();
            this.redraw();
        } finally {
            this.isReloading = false;
            if (this.pendingReload) {
                this.pendingReload = false;
                this.reloadData();
            }
        }
    }

    getCompareMode() {
        const timeScope = UserSession.get('timeScope') || 'month';
        if (timeScope === 'all') {
            return DEFAULT_COMPARE_MODE;
        }
        return 'ROLLING_WINDOW';
    }

    getWindowSize(timeScope) {
        // Dynamically compute comparison window size based on the current timeScope.
        // This determines how many periods to look back for comparison baseline data.
        switch (timeScope) {
            case 'day':
                return 1;  // Compare to the previous day
            case 'week':
                return 1;  // One weekly window (7 days) vs previous weekly window
            case 'month':
                return 1;  // Compare to the same day in the previous month
            case 'quarter':
                return 1;  // Compare to the same day in the previous quarter
            case 'all':
            default:
                return 1;  // Default comparison window
        }
    }

    getDefaultSnapshotAnchor(timeScope) {
        const now = new Date();
        switch (timeScope) {
            case 'day':
                return Utils.getCurrentUTCHour();
            case 'week': {
                const weekStart = Utils.getLocaleWeekStart();
                const localNow = new Date();
                const currentDay = localNow.getDay();
                const diff = (currentDay - weekStart + 7) % 7;
                localNow.setDate(localNow.getDate() - diff);
                return Date.UTC(localNow.getFullYear(), localNow.getMonth(), localNow.getDate(), 0, 0, 0, 0);
            }
            case 'quarter': {
                const quarterStartMonth = Math.floor(now.getUTCMonth() / 3) * 3;
                return Date.UTC(now.getUTCFullYear(), quarterStartMonth, 1, 0, 0, 0, 0);
            }
            case 'all':
            case 'month':
            default:
                return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0);
        }
    }

    resolveColumnPreset(preset) {
        return 'summary';
    }

    resolveColumnDef(col = {}) {
        const resolved = { ...col };
        if (resolved.formatter) {
            resolved.formatter = this[resolved.formatter] || this.constructor.prototype[resolved.formatter] || resolved.formatter;
            if (typeof resolved.formatter !== 'function') {
                delete resolved.formatter;
            } else {
                resolved.formatter = resolved.formatter.bind(this);
            }
        }
        const dashValueFields = new Set([
            'total_hubs',
            'deployed',
            'spare',
            'demo',
            'all_calls',
            'staff_calls',
            'media_shared',
        ]);
        if (!resolved.formatter && dashValueFields.has(String(resolved.field || ''))) {
            resolved.formatter = this.nullDashFormatter;
        }
        if (resolved.sorter) {
            resolved.sorter = this[resolved.sorter] || undefined;
        }
        return resolved;
    }

    nullDashFormatter(row, cell, value) {
        if (row == -1 && cell == -1) {
            return value;
        }
        if (value === MISSING_BASELINE_SENTINEL || value === undefined || value === null || value === '') {
            return '<span style="color: #999;">-</span>';
        }
        return value;
    }

    buildColumnsForPreset() {
        const base = [...this.baseStatsColumns];
        const allFields = new Set(base.map(col => String(col?.field || col?.id || '')));

        return base
            // Collapse standalone delta-percent columns into their paired delta column.
            .filter((col) => !String(col?.field || '').endsWith('_pct'))
            .map((col) => {
                const next = { ...col };
                const field = String(next?.field || '');
                if (field.startsWith('delta_')) {
                    const pctField = `${field}_pct`;
                    if (allFields.has(pctField)) {
                        next.deltaPctField = pctField;
                        next.formatter = this.deltaWithPctFormatter.bind(this);
                    }
                }
                return next;
            });
    }

    deltaWithPctFormatter(row, cell, value, columnDef, dataContext) {
        // Export path: return raw numeric value so Excel columns remain sortable.
        if (row == -1 && cell == -1) {
            if (value === MISSING_BASELINE_SENTINEL || value === undefined || value === null || value === '') return '';
            return value;
        }

        if (value === MISSING_BASELINE_SENTINEL || value === undefined || value === null || value === '') {
            return '<span style="color: #ccc;">—</span>';
        }

        const deltaNum = Number(value);
        if (!Number.isFinite(deltaNum)) {
            return '<span style="color: #ccc;">—</span>';
        }

        const pctRaw = dataContext?.[columnDef?.deltaPctField];
        const pctNum = Number(pctRaw);
        const hasPct = !(pctRaw === MISSING_BASELINE_SENTINEL || pctRaw === undefined || pctRaw === null || pctRaw === '' || !Number.isFinite(pctNum));

        const deltaText = `${deltaNum > 0 ? '+' : ''}${deltaNum}`;
        const pctText = hasPct ? `${pctNum > 0 ? '+' : ''}${pctNum.toFixed(0)}%` : '';
        const merged = hasPct ? `${deltaText} (${pctText})` : deltaText;

        if (deltaNum > 0) {
            return `<span style="color: #22863a; font-weight: 500;">${merged}</span>`;
        }
        if (deltaNum < 0) {
            return `<span style="color: #cb2431; font-weight: 500;">${merged}</span>`;
        }
        return `<span style="color: #999;">${merged}</span>`;
    }

    formatComparisonTimestamp(value, timeScope) {
        const ts = Number(value);
        if (!Number.isFinite(ts) || ts <= 0) {
            return null;
        }
        const d = new Date(ts);
        switch (timeScope) {
            case 'day': {
                // Live/today: timestamps are at the current UTC hour — show date + hour so
                // users see the same-hour-vs-yesterday comparison clearly.
                // Historical day: timestamps are at UTC day boundaries (00:00) because we use
                // daily rollup data — show date only, no misleading hour.
                const isAtDayBoundary = d.getUTCHours() === 0 && d.getUTCMinutes() === 0;
                if (isAtDayBoundary) {
                    return d.toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        timeZone: 'UTC',
                    });
                }
                return d.toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                });
            }
            case 'week':
                // Weekly data — show start-of-week date only
                return d.toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    timeZone: 'UTC',
                });
            case 'month': {
                // Monthly data — show last day of the month
                const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
                return lastDay.toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    timeZone: 'UTC',
                });
            }
            case 'quarter': {
                // Quarterly data — show Q# YYYY
                const quarter = Math.floor(d.getUTCMonth() / 3) + 1;
                return `Q${quarter} ${d.getUTCFullYear()}`;
            }
            case 'all':
            default:
                // Show month + year only
                return d.toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    timeZone: 'UTC',
                });
        }
    }

    // Removed comparisonFormatter; logic will be used for toolbar label instead.
    updateComparisonLabel() {
        const el = this.closest('.hs-grid')?.querySelector('.comparison-label');
        if (el) {
            // Superseded by grouped pre-header labels for snapshot/counter columns.
            el.textContent = '';
            el.style.display = 'none';
        }
    }

    getAnchorMeta(timeScope) {
        const raw = UserSession.get('statsSnapshotAnchorMeta') || {};
        if (raw?.scope !== timeScope) {
            return {
                anchorType: 'calendar',
                isCurrentPeriod: false,
                label: '',
            };
        }
        return {
            anchorType: String(raw.anchorType || 'calendar').toLowerCase(),
            isCurrentPeriod: !!raw.isCurrentPeriod,
            label: String(raw.label || '').trim(),
        };
    }

    formatDateToken(value, { includeHour = false, allowToday = false } = {}) {
        const ts = Number(value);
        if (!Number.isFinite(ts) || ts <= 0) {
            return null;
        }
        const d = new Date(ts);
        const now = new Date();
        const isToday = d.getUTCFullYear() === now.getUTCFullYear()
            && d.getUTCMonth() === now.getUTCMonth()
            && d.getUTCDate() === now.getUTCDate();
        if (allowToday && isToday) {
            return 'Today';
        }
        if (includeHour) {
            return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric' });
        }
        return d.toLocaleString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
    }

    addUtcDays(value, days) {
        const d = new Date(Number(value));
        d.setUTCDate(d.getUTCDate() + Number(days || 0));
        return d.getTime();
    }

    startOfScopeUtc(value, scope) {
        const d = new Date(Number(value));
        if (scope === 'week') {
            const weekStart = Utils.getLocaleWeekStart();
            const currentDay = d.getUTCDay();
            const diff = (currentDay - weekStart + 7) % 7;
            return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - diff, 0, 0, 0, 0);
        }
        if (scope === 'month') {
            return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1, 0, 0, 0, 0);
        }
        if (scope === 'quarter') {
            const quarterStartMonth = Math.floor(d.getUTCMonth() / 3) * 3;
            return Date.UTC(d.getUTCFullYear(), quarterStartMonth, 1, 0, 0, 0, 0);
        }
        return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0);
    }

    endOfScopeUtc(value, scope) {
        const start = this.startOfScopeUtc(value, scope);
        if (scope === 'week') {
            return this.addUtcDays(start, 6);
        }
        if (scope === 'month') {
            const s = new Date(start);
            return Date.UTC(s.getUTCFullYear(), s.getUTCMonth() + 1, 0, 0, 0, 0, 0);
        }
        if (scope === 'quarter') {
            const s = new Date(start);
            return Date.UTC(s.getUTCFullYear(), s.getUTCMonth() + 3, 0, 0, 0, 0, 0);
        }
        return start;
    }

    formatRange(startTs, endTs, { allowTodayEnd = false } = {}) {
        const startText = this.formatDateToken(startTs);
        const endText = this.formatDateToken(endTs, { allowToday: allowTodayEnd });
        if (!startText || !endText) {
            return null;
        }
        return `${startText} to ${endText}`;
    }

    formatTrailingRange(endTs, days, { allowTodayEnd = true } = {}) {
        const end = Number(endTs);
        if (!Number.isFinite(end) || end <= 0) {
            return null;
        }
        const start = this.addUtcDays(end, -Math.max(0, Number(days || 1) - 1));
        return this.formatRange(start, end, { allowTodayEnd });
    }

    getCounterComparisonLabel(timeScope) {
        if (timeScope === 'all') {
            return 'Counter Metrics: All-Time Totals (No Delta)';
        }

        const data = this.currentData && this.currentData.length > 0 ? this.currentData[0] : null;
        const meta = this.getAnchorMeta(timeScope);
        if (data) {
            if (timeScope === 'day' && (data.compare_is_live || (meta.anchorType === 'rolling' && meta.isCurrentPeriod))) {
                return 'Counter Metrics: Daily Totals Through Current Hour: Today vs Yesterday';
            }

            const currentTs = Number(data.compare_current_ts);
            const baselineTs = Number(data.compare_baseline_ts);

            if (timeScope === 'day') {
                const currentText = this.formatDateToken(currentTs);
                const baselineText = this.formatDateToken(baselineTs);
                if (baselineText && currentText) {
                    return `Counter Metrics: End Of Day Totals: ${baselineText} vs ${currentText}`;
                }
            }

            if (timeScope === 'week') {
                if (meta.anchorType === 'rolling') {
                    const baselineRange = this.formatTrailingRange(baselineTs, 7);
                    const currentRange = this.formatTrailingRange(currentTs, 7);
                    if (baselineRange && currentRange) {
                        return `Counter Metrics: 7-Day Running Totals: ${baselineRange} vs ${currentRange}`;
                    }
                }

                const baselineStart = this.startOfScopeUtc(baselineTs, 'week');
                const baselineEnd = this.endOfScopeUtc(baselineTs, 'week');
                const currentStart = this.startOfScopeUtc(currentTs, 'week');
                const currentEnd = meta.isCurrentPeriod ? currentTs : this.endOfScopeUtc(currentTs, 'week');
                const baselineRange = this.formatRange(baselineStart, baselineEnd);
                const currentRange = this.formatRange(currentStart, currentEnd, { allowTodayEnd: meta.isCurrentPeriod });
                if (baselineRange && currentRange) {
                    return `Counter Metrics: Weekly Totals: ${baselineRange} vs ${currentRange}`;
                }
            }

            if (timeScope === 'month') {
                if (meta.anchorType === 'rolling') {
                    const baselineRange = this.formatTrailingRange(baselineTs, 30);
                    const currentRange = this.formatTrailingRange(currentTs, 30);
                    if (baselineRange && currentRange) {
                        return `Counter Metrics: 30-Day Running Totals: ${baselineRange} vs ${currentRange}`;
                    }
                }

                const baselineStart = this.startOfScopeUtc(baselineTs, 'month');
                const currentStart = this.startOfScopeUtc(currentTs, 'month');
                const baselineEnd = meta.isCurrentPeriod ? baselineTs : this.endOfScopeUtc(baselineTs, 'month');
                const currentEnd = meta.isCurrentPeriod ? currentTs : this.endOfScopeUtc(currentTs, 'month');
                const baselineRange = this.formatRange(baselineStart, baselineEnd);
                const currentRange = this.formatRange(currentStart, currentEnd, { allowTodayEnd: meta.isCurrentPeriod });
                if (baselineRange && currentRange) {
                    return `Counter Metrics: Monthly Totals: ${baselineRange} vs ${currentRange}`;
                }
            }

            if (timeScope === 'quarter') {
                if (meta.anchorType === 'rolling') {
                    const baselineRange = this.formatTrailingRange(baselineTs, 90);
                    const currentRange = this.formatTrailingRange(currentTs, 90);
                    if (baselineRange && currentRange) {
                        return `Counter Metrics: 90-Day Running Totals: ${baselineRange} vs ${currentRange}`;
                    }
                }

                const baselineStart = this.startOfScopeUtc(baselineTs, 'quarter');
                const currentStart = this.startOfScopeUtc(currentTs, 'quarter');
                const baselineEnd = meta.isCurrentPeriod ? baselineTs : this.endOfScopeUtc(baselineTs, 'quarter');
                const currentEnd = meta.isCurrentPeriod ? currentTs : this.endOfScopeUtc(currentTs, 'quarter');
                const baselineRange = this.formatRange(baselineStart, baselineEnd);
                const currentRange = this.formatRange(currentStart, currentEnd, { allowTodayEnd: meta.isCurrentPeriod });
                if (baselineRange && currentRange) {
                    return `Counter Metrics: Quarterly Totals: ${baselineRange} vs ${currentRange}`;
                }
            }

            const currentText = this.formatComparisonTimestamp(data.compare_current_ts, timeScope);
            const baselineText = this.formatComparisonTimestamp(data.compare_baseline_ts, timeScope);
            if (baselineText && currentText) {
                return `Counter Metrics: Totals from ${baselineText} vs Totals from ${currentText}`;
            }

            const fallbackLabel = String(data.compare_label || '').trim();
            if (fallbackLabel) {
                return `Counter Metrics: ${fallbackLabel}`;
            }
        }

        return 'Counter Metrics: Totals from Earlier Period vs Totals from Later Period';
    }

    getCounterComparisonTooltip(timeScope) {
        if (timeScope === 'all') {
            return 'Counter metrics show all-time accumulated totals. Deltas are intentionally disabled for all-time scope.';
        }

        const data = this.currentData && this.currentData.length > 0 ? this.currentData[0] : null;
        const meta = this.getAnchorMeta(timeScope);
        if (data) {
            if (timeScope === 'day' && (data.compare_is_live || (meta.anchorType === 'rolling' && meta.isCurrentPeriod))) {
                return 'Counter metrics compare totals through the current UTC hour today against totals through the same UTC hour yesterday.';
            }

            const currentTs = Number(data.compare_current_ts);
            const baselineTs = Number(data.compare_baseline_ts);

            if (timeScope === 'week') {
                if (meta.anchorType === 'rolling') {
                    const baselineRange = this.formatTrailingRange(baselineTs, 7);
                    const currentRange = this.formatTrailingRange(currentTs, 7);
                    if (baselineRange && currentRange) {
                        return `Counter metrics compare 7-day running totals: ${baselineRange} vs ${currentRange}.`;
                    }
                }
                const baselineStart = this.startOfScopeUtc(baselineTs, 'week');
                const baselineEnd = this.endOfScopeUtc(baselineTs, 'week');
                const currentStart = this.startOfScopeUtc(currentTs, 'week');
                const currentEnd = meta.isCurrentPeriod ? currentTs : this.endOfScopeUtc(currentTs, 'week');
                const baselineRange = this.formatRange(baselineStart, baselineEnd);
                const currentRange = this.formatRange(currentStart, currentEnd, { allowTodayEnd: meta.isCurrentPeriod });
                if (baselineRange && currentRange) {
                    return `Counter metrics compare weekly totals: ${baselineRange} vs ${currentRange}.`;
                }
            }

            if (timeScope === 'month') {
                if (meta.anchorType === 'rolling') {
                    const baselineRange = this.formatTrailingRange(baselineTs, 30);
                    const currentRange = this.formatTrailingRange(currentTs, 30);
                    if (baselineRange && currentRange) {
                        return `Counter metrics compare 30-day running totals: ${baselineRange} vs ${currentRange}.`;
                    }
                }
                const baselineStart = this.startOfScopeUtc(baselineTs, 'month');
                const currentStart = this.startOfScopeUtc(currentTs, 'month');
                const baselineEnd = meta.isCurrentPeriod ? baselineTs : this.endOfScopeUtc(baselineTs, 'month');
                const currentEnd = meta.isCurrentPeriod ? currentTs : this.endOfScopeUtc(currentTs, 'month');
                const baselineRange = this.formatRange(baselineStart, baselineEnd);
                const currentRange = this.formatRange(currentStart, currentEnd, { allowTodayEnd: meta.isCurrentPeriod });
                if (baselineRange && currentRange) {
                    return `Counter metrics compare monthly totals: ${baselineRange} vs ${currentRange}.`;
                }
            }

            if (timeScope === 'quarter') {
                if (meta.anchorType === 'rolling') {
                    const baselineRange = this.formatTrailingRange(baselineTs, 90);
                    const currentRange = this.formatTrailingRange(currentTs, 90);
                    if (baselineRange && currentRange) {
                        return `Counter metrics compare 90-day running totals: ${baselineRange} vs ${currentRange}.`;
                    }
                }
                const baselineStart = this.startOfScopeUtc(baselineTs, 'quarter');
                const currentStart = this.startOfScopeUtc(currentTs, 'quarter');
                const baselineEnd = meta.isCurrentPeriod ? baselineTs : this.endOfScopeUtc(baselineTs, 'quarter');
                const currentEnd = meta.isCurrentPeriod ? currentTs : this.endOfScopeUtc(currentTs, 'quarter');
                const baselineRange = this.formatRange(baselineStart, baselineEnd);
                const currentRange = this.formatRange(currentStart, currentEnd, { allowTodayEnd: meta.isCurrentPeriod });
                if (baselineRange && currentRange) {
                    return `Counter metrics compare quarterly totals: ${baselineRange} vs ${currentRange}.`;
                }
            }

            const currentText = this.formatComparisonTimestamp(data.compare_current_ts, timeScope);
            const baselineText = this.formatComparisonTimestamp(data.compare_baseline_ts, timeScope);
            if (baselineText && currentText) {
                return `Counter metrics compare totals from ${baselineText} against totals from ${currentText}.`;
            }
        }

        return 'Counter metrics compare totals from an earlier baseline period against totals from a later selected period.';
    }

    getSnapshotComparisonLabel(timeScope) {
        if (timeScope === 'all') {
            return 'Snapshot Metrics: Current All-Time Values (No Delta)';
        }

        const data = this.currentData && this.currentData.length > 0 ? this.currentData[0] : null;
        const meta = this.getAnchorMeta(timeScope);
        if (!data) {
            return 'Snapshot Metrics';
        }

        const currentTs = Number(data.compare_current_ts);
        const baselineTs = Number(data.compare_baseline_ts);

        if (timeScope === 'day') {
            if (data.compare_is_live || (meta.anchorType === 'rolling' && meta.isCurrentPeriod)) {
                return 'Snapshot Metrics: Now vs Same Hour Yesterday';
            }
            const currentText = this.formatDateToken(currentTs);
            const baselineText = this.formatDateToken(baselineTs);
            if (baselineText && currentText) {
                return `Snapshot Metrics: End Of Day: ${baselineText} vs ${currentText}`;
            }
        }

        // Snapshot metrics are discrete point-in-time values, never accumulated ranges.
        if (timeScope === 'week' && meta.anchorType === 'rolling' && meta.isCurrentPeriod) {
            return 'Snapshot Metrics: Today vs Same Day Last Week';
        }

        const baselineText = this.formatDateToken(baselineTs);
        const currentText = this.formatDateToken(currentTs, { allowToday: meta.isCurrentPeriod || (meta.anchorType === 'rolling') });
        if (baselineText && currentText) {
            return `Snapshot Metrics: ${baselineText} vs ${currentText}`;
        }

        const liveLabel = String(data.compare_label || '').trim();
        return liveLabel ? `Snapshot Metrics: ${liveLabel}` : 'Snapshot Metrics';
    }

    getSnapshotComparisonTooltip(timeScope) {
        if (timeScope === 'all') {
            return 'Snapshot metrics show current point-in-time values only. Deltas are intentionally disabled for all-time scope.';
        }

        const data = this.currentData && this.currentData.length > 0 ? this.currentData[0] : null;
        const meta = this.getAnchorMeta(timeScope);
        if (!data) {
            return 'Snapshot metrics compare a current point-in-time value against a baseline point-in-time value.';
        }

        if (timeScope === 'day' && (data.compare_is_live || (meta.anchorType === 'rolling' && meta.isCurrentPeriod))) {
            return 'Snapshot metrics compare the current point-in-time value against the same UTC hour yesterday.';
        }

        if (timeScope === 'week' && meta.anchorType === 'rolling' && meta.isCurrentPeriod) {
            return 'Snapshot metrics compare today\'s point-in-time value against the same day last week.';
        }

        const currentText = this.formatDateToken(data.compare_current_ts, { allowToday: meta.isCurrentPeriod || (meta.anchorType === 'rolling') });
        const baselineText = this.formatDateToken(data.compare_baseline_ts);
        if (currentText && baselineText) {
            return `Snapshot metrics compare point-in-time values: ${currentText} vs ${baselineText}.`;
        }
        return 'Snapshot metrics compare a current point-in-time value against a baseline point-in-time value.';
    }

    updatePreHeaderGroups() {
        if (!this.table) {
            return;
        }

        const cols = this.table.getColumns() || [];
        if (cols.length < 1) {
            this.setPreHeaderGroups([]);
            return;
        }

        const timeScope = UserSession.get('timeScope') || 'month';
        const snapshotMetricFields = new Set([
            'total_hubs', 'deployed', 'spare', 'demo',
            'delta_hubs', 'delta_hubs_pct',
            'delta_deployed', 'delta_deployed_pct',
            'delta_spare', 'delta_spare_pct',
            'delta_demo', 'delta_demo_pct',
        ]);
        const counterMetricFields = new Set([
            'all_calls', 'staff_calls', 'media_shared',
            'delta_all_calls', 'delta_all_calls_pct',
            'delta_staff_calls', 'delta_staff_calls_pct',
            'delta_media_shared', 'delta_media_shared_pct',
        ]);

        const providerCols = cols
            .filter(col => String(col?.field || col?.id || '') === 'provider')
            .map(col => String(col?.field || col?.id || ''));
        const snapshotCols = cols
            .filter(col => snapshotMetricFields.has(String(col?.field || col?.id || '')))
            .map(col => String(col?.field || col?.id || ''));
        const counterCols = cols
            .filter(col => counterMetricFields.has(String(col?.field || col?.id || '')))
            .map(col => String(col?.field || col?.id || ''));

        const groups = [];
        if (providerCols.length > 0) {
            groups.push({
                id: 'provider',
                label: 'Provider',
                tooltip: 'Provider name for each row.',
                columns: providerCols,
                className: 'hs-preheader-provider',
                align: 'left',
            });
        }
        if (snapshotCols.length > 0) {
            groups.push({
                id: 'snapshot',
                label: this.getSnapshotComparisonLabel(timeScope),
                tooltip: this.getSnapshotComparisonTooltip(timeScope),
                columns: snapshotCols,
                className: 'hs-preheader-snapshot',
            });
        }
        if (counterCols.length > 0) {
            groups.push({
                id: 'counter',
                label: this.getCounterComparisonLabel(timeScope),
                tooltip: this.getCounterComparisonTooltip(timeScope),
                columns: counterCols,
                className: 'hs-preheader-counter',
            });
        }

        this.setPreHeaderGroups(groups, { height: 44 });
    }

    applyColumnPreset(preset) {
        const resolvedPreset = this.resolveColumnPreset(preset);
        this.activeColumnPreset = resolvedPreset;
        UserSession.set('statsColumnPreset', resolvedPreset);
        if (!this.table || this.baseStatsColumns.length < 1) {
            return;
        }
        const cols = this.buildColumnsForPreset();
        if (cols.length < 1) {
            return;
        }
        this.table.setColumns(cols);
        this.updateComparisonLabel();
        this.updatePreHeaderGroups();
    }

    metricWithDeltaFormatter(row, cell, value, columnDef, dataContext) {
        const isTotalsRow = !!dataContext?._isTotalsRow;
        const hasValue = !(value === undefined || value === null || value === '' || value === MISSING_BASELINE_SENTINEL);
        const rawNum = hasValue ? Number(value) : null;
        const numberFormatter = new Intl.NumberFormat('en-US');
        const valueText = hasValue
            ? (Number.isFinite(rawNum) ? numberFormatter.format(rawNum) : String(value))
            : '—';

        const deltaValue = dataContext?.[columnDef?.deltaField];
        const deltaPctValue = dataContext?.[columnDef?.deltaPctField];
        const deltaMissing = deltaValue === MISSING_BASELINE_SENTINEL || deltaValue === undefined || deltaValue === null || deltaValue === '';
        const pctMissing = deltaPctValue === MISSING_BASELINE_SENTINEL || deltaPctValue === undefined || deltaPctValue === null || deltaPctValue === '';

        // Delta trend badges are only rendered on the aggregate totals row; per-provider rows show the value only.
        const showDelta = isTotalsRow && !(deltaMissing && pctMissing);

        if (row == -1 && cell == -1) {
            if (!showDelta) {
                return valueText;
            }
            const exportDelta = deltaMissing ? '' : `${Number(deltaValue) > 0 ? '+' : ''}${numberFormatter.format(Number(deltaValue))}`;
            const exportPct = pctMissing ? '' : `${Number(deltaPctValue) > 0 ? '+' : ''}${Number(deltaPctValue).toFixed(1)}%`;
            if (exportDelta === '' && exportPct === '') {
                return valueText;
            }
            return `${valueText} (${exportDelta}${exportDelta && exportPct ? ' / ' : ''}${exportPct})`;
        }

        if (!showDelta) {
            return isTotalsRow
                ? `<span style="font-weight: 700;">${valueText}</span>`
                : valueText;
        }

        const deltaNum = Number(deltaValue);
        const pctNum = Number(deltaPctValue);
        const hasDelta = !deltaMissing && Number.isFinite(deltaNum);
        const hasPct = !pctMissing && Number.isFinite(pctNum);
        const deltaPart = hasDelta ? `${deltaNum > 0 ? '+' : ''}${numberFormatter.format(deltaNum)}` : '';
        // Cap displayed percentage at |999%| so extreme mock/edge values don't dominate the cell.
        let pctPart = '';
        if (hasPct) {
            if (Math.abs(pctNum) > 999) {
                pctPart = pctNum > 0 ? '>999%' : '<-999%';
            } else {
                pctPart = `${pctNum > 0 ? '+' : ''}${pctNum.toFixed(1)}%`;
            }
        }
        let deltaText = '';
        if (deltaPart && pctPart) {
            deltaText = `(${deltaPart} / ${pctPart})`;
        } else if (deltaPart) {
            deltaText = `(${deltaPart})`;
        } else if (pctPart) {
            deltaText = `(${pctPart})`;
        }

        let deltaColor = '#999';
        if (hasDelta && deltaNum > 0) {
            deltaColor = '#22863a';
        } else if (hasDelta && deltaNum < 0) {
            deltaColor = '#cb2431';
        }
        return `<span style="font-weight: 700;">${valueText}</span>${deltaText ? ` <span style="color: ${deltaColor}; font-weight: 500;">${deltaText}</span>` : ''}`;
    }

    deltaFormatter(row, cell, value, columnDef, dataContext) {
        // handle data export
        if (row == -1 && cell == -1) { 
            return value; 
        }

        if (value === MISSING_BASELINE_SENTINEL) {
            return '<span style="color: #ccc;" title="No data for baseline period">—</span>';
        }

        if (value === undefined || value === null) {
            return '<span style="color: #ccc;">—</span>';
        }

        const numValue = Number(value);
        if (isNaN(numValue)) {
            return '<span style="color: #ccc;">—</span>';
        }

        // Format with sign (+/-)
        const sign = numValue > 0 ? '+' : '';
        const displayValue = sign + numValue;

        // Color based on delta value
        if (numValue > 0) {
            return '<span style="color: #22863a; font-weight: 500;">' + displayValue + '</span>';
        } else if (numValue < 0) {
            return '<span style="color: #cb2431; font-weight: 500;">' + displayValue + '</span>';
        } else {
            return '<span style="color: #999;">0</span>';
        }
    }

    deltaPctFormatter(row, cell, value) {
        if (row == -1 && cell == -1) {
            return value;
        }

        if (value === MISSING_BASELINE_SENTINEL || value === undefined || value === null || value === '') {
            return '<span style="color: #ccc;">—</span>';
        }

        const numValue = Number(value);
        if (!Number.isFinite(numValue)) {
            return '<span style="color: #ccc;">—</span>';
        }

        const rounded = numValue.toFixed(0);
        const sign = numValue > 0 ? '+' : '';
        const displayValue = `${sign}${rounded}%`;
        if (numValue > 0) {
            return '<span style="color: #22863a; font-weight: 500;">' + displayValue + '</span>';
        }
        if (numValue < 0) {
            return '<span style="color: #cb2431; font-weight: 500;">' + displayValue + '</span>';
        }
        return '<span style="color: #999;">0%</span>';
    }

    statusFormatter(row, cell, value, columnDef, dataContext) {
        if (row == -1 && cell == -1) {
            return value;
        }

        const status = String(value || 'Unknown');
        const completeness = Number(dataContext?.completeness_pct);
        const note = Number.isFinite(completeness)
            ? ` (${completeness.toFixed(0)}% complete)`
            : '';

        if (status === 'Complete') {
            return `<span title="Full comparison window">Complete</span>`;
        }
        if (status === 'Projected') {
            return `<span title="Projected from partial window${note}">Projected</span>`;
        }
        if (status === 'In progress') {
            return `<span title="Partial period comparison${note}">In progress</span>`;
        }
        return `<span title="Insufficient baseline history">Insufficient data</span>`;
    }

    compareLabelFormatter(row, cell, value) {
        if (row == -1 && cell == -1) {
            return value;
        }
        return value || '—';
    }

    asOfFormatter(row, cell, value) {
        const ts = Number(value);
        if (!Number.isFinite(ts) || ts <= 0) {
            return row == -1 && cell == -1 ? '' : '—';
        }
        return new Date(ts).toLocaleString();
    }

    getExportData() {
        // Columns marked exportOnly are included in the export but hidden from the table.
        const exportOnlyCols = (this.config.cols || [])
            .filter(col => col.exportOnly)
            .map(col => ({ header: col.name, key: col.field, width: col.width || 30, context: null }));

        const activeCols = (this.table?.getColumns() || [])
            .filter(col => !col.skipExport)
            .map(col => ({ header: col.name, key: col.field, width: 30, context: col }));

        const cols = [...exportOnlyCols, ...activeCols];

        const data = this.dataView?.getItems() || this.currentData || [];
        const rows = data.map(row =>
            cols.reduce((accum, col) => {
                let value;
                if (col.context?.formatter) {
                    value = col.context.formatter(-1, -1, row[col.key], col.context, row);
                } else {
                    value = row[col.key];
                }
                // Replace the missing-baseline sentinel with empty so it doesn't leak into exports.
                if (value === MISSING_BASELINE_SENTINEL) value = '';
                accum[col.key] = value;
                return accum;
            }, {})
        );

        // Build a comparison header with actual dates rather than the live relative label,
        // so exported reports are self-describing when reviewed later.
        const firstRow = data[0] || null;
        const timeScope = UserSession.get('timeScope') || 'month';
        let meta = [];
        if (firstRow) {
            const currentText = this.formatComparisonTimestamp(firstRow.compare_current_ts, timeScope);
            const baselineText = this.formatComparisonTimestamp(firstRow.compare_baseline_ts, timeScope);
            const fallbackLabel = String(firstRow.compare_label || '').trim();
            const dateRange = (currentText && baselineText)
                ? `${currentText} vs ${baselineText}`
                : fallbackLabel;
            if (dateRange) {
                meta = [`Comparison: ${dateRange}`];
            }
        }

        return { name: this.config.exportTitle || 'Stats', cols, rows, meta };
    }

    async load() {
        //
        // if the columns are defined, set them now
        //
        if (this.config.cols) {
            this.baseStatsColumns = this.config.cols
                .filter(col => !col.exportOnly)
                .map(col => this.resolveColumnDef(col));
            if (typeof this.table?.setOptions === 'function') {
                this.table.setOptions({
                    forceFitColumns: true,
                    enableAutoSizeColumns: false,
                });
            }
            this.applyColumnPreset();
            this.updatePreHeaderGroups();
        }

        // Initialize compare mode picker
        this.initializeCompareModePickerUI();

        this.reloadData();
    }

    initializeCompareModePickerUI() {
        if ((this.config?.reportId ?? 'providers_stats').trim() === 'providers_stats') {
            return;
        }

        // Create picker container
        const pickerHTML = `
            <div class="stats-compare-mode-picker" title="Change comparison baseline strategy">
                <label for="compare-mode-select">Compare:</label>
                <select id="compare-mode-select" style="padding: 4px 8px; border-radius: 4px; border: 1px solid #ddd; font-size: 12px; cursor: pointer;">
                    <option value="LAST_COMPLETE">Last Complete</option>
                    <option value="PARTIAL_ALIGNED">Partial Aligned</option>
                    <option value="ROLLING_WINDOW">Rolling Window</option>
                    <option value="PROJECTED_FULL">Projected Full</option>
                </select>
            </div>
        `;

        // Find card-actions and insert picker
        const cardActions = this.querySelector('.card-actions');
        if (cardActions && !cardActions.querySelector('.stats-compare-mode-picker')) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = pickerHTML;
            const picker = tempDiv.firstElementChild;
            
            // Insert at the beginning of card-actions (before other buttons)
            cardActions.insertBefore(picker, cardActions.firstChild);

            // Set current value
            const select = this.querySelector('#compare-mode-select');
            if (select) {
                select.value = this.getCompareMode();
                
                // Add change listener
                select.addEventListener('change', (e) => {
                    const newMode = e.target.value;
                    Logger.debug('Compare mode changed to:', newMode);
                    UserSession.set('statsCompareMode', newMode);
                    this.reloadData();
                });
            }
        }
    }

}
customElements.define("hs-stats-table", HSStatsTable);
